import { ARCHITECTURES, ARCHITECTURE_IDS, type ArchitectureId } from '../demo/architecture'
import { Disclosure } from './Disclosure'
import { sx } from '../lib/sx'

/**
 * The switch that re-tells the whole scenario under a different data model,
 * and — behind one chevron — the case for and against it.
 *
 * Sits at the top of the sidebar: it frames everything below it. The argument
 * stays collapsed so the flow controls are not pushed off screen by reference
 * material nobody is reading mid-demo.
 */
export function ArchitecturePanel({
  value,
  onChange,
}: {
  value: ArchitectureId
  onChange: (id: ArchitectureId) => void
}) {
  const arch = ARCHITECTURES[value]

  return (
    <section style={sx('display:flex;flex-direction:column;gap:10px')}>
      <SectionLabel>Architecture</SectionLabel>

      <div
        style={sx(
          'display:flex;gap:4px;padding:4px;border-radius:var(--radius-full);background:var(--bg-tertiary)'
        )}
      >
        {ARCHITECTURE_IDS.map((id) => {
          const on = id === value
          return (
            <button
              key={id}
              onClick={() => onChange(id)}
              style={sx(
                'flex:1;min-width:0;padding:6px 8px;border:none;border-radius:var(--radius-full);' +
                  'cursor:pointer;font-family:inherit;display:flex;flex-direction:column;' +
                  'align-items:center;justify-content:center;gap:1px;' +
                  `background:${on ? 'var(--bg-accent)' : 'transparent'};` +
                  'transition:all 150ms var(--ease-out)'
              )}
            >
              <span
                style={sx(
                  'font-size:var(--fs-xs);font-weight:var(--fw-medium);line-height:16px;' +
                    `color:${on ? '#fff' : 'var(--text-secondary)'}`
                )}
              >
                {ARCHITECTURES[id].name}
              </span>
              {/* The qualifier is the actual distinction, so it earns its own line
                  rather than being squeezed into a parenthetical that will not fit. */}
              <span
                style={sx(
                  'font-size:var(--fs-2xs);line-height:14px;' +
                    `color:${on ? 'rgba(255,255,255,.75)' : 'var(--text-disabled)'}`
                )}
              >
                {ARCHITECTURES[id].qualifier}
              </span>
            </button>
          )
        })}
      </div>

      {/*
        A fixed header, so the row does not rewrite itself every time the switch
        moves — the selected model is already obvious from the pills. Not keyed
        on the architecture either: once open, flipping the switch swaps the
        contents in place, which is how you compare the two models.
      */}
      <Disclosure label="Architecture details">
        <div style={sx('display:flex;flex-direction:column;gap:12px')}>
          <span
            style={sx(
              'font-size:var(--fs-md);font-weight:var(--fw-bold);letter-spacing:var(--ls-sm)'
            )}
          >
            {arch.tagline}
          </span>
          <span style={sx('font-size:var(--fs-xs);line-height:18px;color:var(--text-secondary)')}>
            {arch.summary}
          </span>

          <div className="demo-arch-cards">
            <Card title="Backend stores" tone="neutral" items={arch.serverStores} />
            <Card title="Backend never learns" tone="success" items={arch.serverNeverStores} />
            <Card title="Buys you" tone="success" items={arch.strengths} />
            <Card title="Costs you" tone="critical" items={arch.costs} />
          </div>

          <span style={sx('font-size:var(--fs-2xs);line-height:16px;color:var(--text-disabled)')}>
            Switching model replays the current flow — an attributed alert feed cannot survive a
            move to zero knowledge.
          </span>
        </div>
      </Disclosure>
    </section>
  )
}

function Card({
  title,
  items,
  tone,
}: {
  title: string
  items: string[]
  tone: 'neutral' | 'success' | 'critical'
}) {
  const dot =
    tone === 'success'
      ? 'var(--text-success)'
      : tone === 'critical'
        ? 'var(--text-critical)'
        : 'var(--text-secondary)'

  return (
    <div
      style={sx(
        'display:flex;flex-direction:column;gap:8px;padding:14px;border-radius:var(--radius-md);' +
          'background:var(--bg-secondary);border:1px solid var(--border-secondary)'
      )}
    >
      <span
        style={sx(
          'font-size:var(--fs-2xs);font-weight:var(--fw-medium);color:var(--text-secondary);' +
            'letter-spacing:.04em;text-transform:uppercase'
        )}
      >
        {title}
      </span>
      {items.map((item) => (
        <span key={item} style={sx('display:flex;gap:8px;align-items:flex-start')}>
          <span
            style={sx(
              `width:5px;height:5px;flex:none;margin-top:6px;border-radius:var(--radius-full);background:${dot}`
            )}
          />
          <span style={sx('font-size:var(--fs-xs);line-height:17px;color:var(--text-primary)')}>
            {item}
          </span>
        </span>
      ))}
    </div>
  )
}

export function SectionLabel({ children }: { children: string }) {
  return (
    <span
      style={sx(
        'font-size:var(--fs-2xs);font-weight:var(--fw-medium);color:var(--text-secondary);' +
          'letter-spacing:.06em;text-transform:uppercase'
      )}
    >
      {children}
    </span>
  )
}
